import { useRef } from "react";
import { CrmContactChannelsEditor } from "@/components/crm/CrmContactChannelsEditor";
import { CrmSelect, crmSelectFieldWrapperClass } from "@/components/crm/CrmListChevron";
import {
  normalizeDomainFieldInput,
  nextDomainAfterWebsiteChange,
} from "@/lib/crmDomainFromWebsite";
import type { CrmAddressTypeOptions } from "@/lib/crmContactAddresses";
import {
  emptyChannelsState,
  type CrmChannelTypeOptions,
  type CrmContactChannelsState,
} from "@/lib/crmContactChannels";
import {
  crmLeadFieldVisibleOnNewLead,
  type CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFields";

export function LeadContactFields({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  title,
  setTitle,
  channels,
  setChannels,
  channelOptions,
  defaultPhoneCountryId,
  addressTypeOptions,
  defaultAddressCountryId,
  embedded,
}: {
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  title: string;
  setTitle: (v: string) => void;
  channels: CrmContactChannelsState;
  setChannels: (v: CrmContactChannelsState) => void;
  channelOptions: CrmChannelTypeOptions;
  defaultPhoneCountryId: string;
  addressTypeOptions: CrmAddressTypeOptions;
  defaultAddressCountryId: string;
  embedded?: boolean;
}) {
  return (
    <div className={embedded ? "space-y-4 pt-1" : "space-y-4"}>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="sm:col-span-2 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
      </div>
      <CrmContactChannelsEditor
        value={channels}
        onChange={setChannels}
        emailContext="contact"
        typeOptions={channelOptions}
        defaultPhoneCountryId={defaultPhoneCountryId}
        addressTypeOptions={addressTypeOptions}
        defaultAddressCountryId={defaultAddressCountryId}
      />
    </div>
  );
}

export function LeadAccountFields({
  companyName,
  setCompanyName,
  companyDomain,
  setCompanyDomain,
  website,
  setWebsite,
  industry,
  setIndustry,
  industrySegment,
  setIndustrySegment,
  industryOptions,
  industrySegmentOptions,
  channels,
  setChannels,
  channelOptions,
  defaultPhoneCountryId,
  addressTypeOptions,
  defaultAddressCountryId,
  compact,
  leadFieldSections,
  companyNameRequired = true,
}: {
  companyName: string;
  setCompanyName: (v: string) => void;
  companyDomain: string;
  setCompanyDomain: (v: string) => void;
  website: string;
  setWebsite: (v: string) => void;
  industry: string;
  setIndustry: (v: string) => void;
  industrySegment: string;
  setIndustrySegment: (v: string) => void;
  industryOptions: { value: string; label: string }[];
  industrySegmentOptions: { value: string; label: string }[];
  channels: CrmContactChannelsState;
  setChannels: (v: CrmContactChannelsState) => void;
  channelOptions: CrmChannelTypeOptions;
  defaultPhoneCountryId: string;
  addressTypeOptions: CrmAddressTypeOptions;
  defaultAddressCountryId: string;
  compact?: boolean;
  leadFieldSections: CrmLeadModuleFieldsDocument["sections"] | null;
  companyNameRequired?: boolean;
}) {
  const show = (fieldId: string) =>
    crmLeadFieldVisibleOnNewLead(leadFieldSections, "account_lead", fieldId);
  const showCompanyName = show("company_name");
  const showIndustry = show("industry");
  const showIndustrySegment = show("industry_segment");
  const showWebsite = show("website");
  const showDomain = show("domain");
  const showIndustryColumn = showIndustry || showIndustrySegment;
  const showWebColumn = showWebsite || showDomain;

  const lastAutoDerivedDomainRef = useRef("");

  const handleWebsiteChange = (value: string) => {
    setWebsite(value);
    const next = nextDomainAfterWebsiteChange(value, companyDomain, lastAutoDerivedDomainRef.current);
    if (next.domain !== companyDomain) {
      setCompanyDomain(next.domain);
    }
    lastAutoDerivedDomainRef.current = next.lastAutoDerivedDomain;
  };

  const handleDomainChange = (value: string) => {
    setCompanyDomain(value);
  };

  const handleDomainBlur = () => {
    const normalized = normalizeDomainFieldInput(companyDomain);
    if (normalized !== companyDomain) {
      setCompanyDomain(normalized);
    }
  };

  return (
    <fieldset className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-white/10">
      <legend className="px-1 text-sm font-semibold">Lead Account</legend>
      {showCompanyName ? (
        <input
          required={companyNameRequired && !compact}
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Company name"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
      ) : null}
      {showIndustryColumn || showWebColumn ? (
        <div
          className={
            showIndustryColumn && showWebColumn ? "grid gap-3 sm:grid-cols-2" : "space-y-3"
          }
        >
          {showIndustryColumn ? (
            <div className="flex flex-col gap-3">
              {showIndustry ? (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="sr-only">Industry</span>
                  <CrmSelect
                    value={industry}
                    onChange={(e) => {
                      setIndustry(e.target.value);
                      setIndustrySegment("");
                    }}
                    aria-label="Industry"
                    wrapperClassName={crmSelectFieldWrapperClass}
                    className="bg-transparent py-2 pl-3 text-sm"
                  >
                    <option value="">Industry</option>
                    {industryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </CrmSelect>
                </label>
              ) : null}
              {showIndustrySegment ? (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="sr-only">Industry segment</span>
                  <CrmSelect
                    value={industrySegment}
                    disabled={!industry}
                    onChange={(e) => setIndustrySegment(e.target.value)}
                    aria-label="Industry segment"
                    wrapperClassName={crmSelectFieldWrapperClass}
                    className="bg-transparent py-2 pl-3 text-sm disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {industry ? "Industry segment (optional)" : "Select industry first"}
                    </option>
                    {industrySegmentOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </CrmSelect>
                </label>
              ) : null}
            </div>
          ) : null}
          {showWebColumn ? (
            <div className="flex flex-col gap-3">
              {showWebsite ? (
                <input
                  value={website}
                  onChange={(e) => handleWebsiteChange(e.target.value)}
                  placeholder="Website (https://example.com)"
                  inputMode="url"
                  autoComplete="url"
                  aria-label="Website"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
                />
              ) : null}
              {showDomain ? (
                <input
                  value={companyDomain}
                  onChange={(e) => handleDomainChange(e.target.value)}
                  onBlur={handleDomainBlur}
                  placeholder="Domain (example.com)"
                  autoComplete="off"
                  aria-label="Domain"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <CrmContactChannelsEditor
        value={channels}
        onChange={setChannels}
        emailContext="account"
        typeOptions={channelOptions}
        defaultPhoneCountryId={defaultPhoneCountryId}
        addressTypeOptions={addressTypeOptions}
        defaultAddressCountryId={defaultAddressCountryId}
      />
    </fieldset>
  );
}

export { emptyChannelsState };
